import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { Form } from "@/components/ui/form"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "@/components/ui/button"
import { ButtonVariant } from "@/constants/button/button"
import { AppDispatch } from "@/redux"
import { useDispatch } from "react-redux"
import { getFormProps } from "@/helpers/formHelpers"
import { FilterProps } from "@/types/workflow"
import { workflowActions } from "@/redux/slice/workflowSlice"
import { FilterCode } from "@/constants/filters/filters"
import { useEffect } from "react"
import { TextInputField } from "@/components/custom/InputField/TextInputField"

const formSchema = z.object({
  filterValue: z.string(),
})

export const TextboxFilter: React.FC<FilterProps> = ({
  code,
  stepId,
  order,
  selectedValue,
  name,
  description
}: FilterProps) => {
  const dispatch: AppDispatch = useDispatch()

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      filterValue: ''
    },
  })
  const filterValue = form.watch('filterValue')
  const formProps = getFormProps(form, formSchema)

  useEffect(() => {
    if (selectedValue) {
      form.setValue('filterValue', selectedValue)
    }
  }, [selectedValue, form])

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          className="w-auto font-semibold"
          variant={ButtonVariant.DEFAULT}
        >
          Update
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="mb-6">
            <div className="flex flex-row items-start justify-between">
              <div className="flex flex-col items-start">
                <h4 className="text-primary font-semibold text-xl leading-5 mb-2">
                  Filter Setup: {name}
                </h4>
                <p className="text-secondary text-sm mb-2 text-left font-normal">{description}</p>
              </div>
              <div className="flex items-center">
                <AlertDialogCancel className="mx-4">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  type="submit"
                  disabled={filterValue?.length === 0}
                  onClick={() => {
                    if (filterValue?.length > 0) {
                      dispatch(workflowActions.setStepFilter({
                        stepId,
                        order,
                        filter: code,
                        filterValue
                      }))
                      dispatch(workflowActions.setLastUpdatedFilter(code as FilterCode))
                    }
                  }}
                >
                  Update Filter
                </AlertDialogAction>
              </div>
            </div>
          </AlertDialogTitle>
          <div className="">
            <div className="my-4" />
            <Form {...form}>
              <form className="space-y-6 flex flex-col items-start">
                <TextInputField
                  name="filterValue"
                  label={name}
                  error={!!form.formState.errors.filterValue}
                  additionalClassName="w-[460px] bg-foreground"
                  {...formProps}
                />
              </form>
            </Form>
          </div>
        </AlertDialogHeader>
      </AlertDialogContent>
    </AlertDialog>
  )
}
